import { isScheduledTaskOwnedByExcluded } from "./bundle-ownership";
import {
  ScheduledTaskEntry,
  ScheduledTaskOverride,
  ScheduledTasksConfig,
} from "../agent-config-options";

/**
 * Recommended model labels surfaced on the scheduled-task SKILL.md
 * frontmatter. The labels are **informational** — configulator does not
 * pin a model per task (the Claude Code scheduled-task runtime does not
 * support per-task model pinning yet). Operators choose the model at
 * invocation time; splitting workers by type still gives independent
 * cadence and clean opt-in/opt-out control.
 *
 * @see ScheduledTasksConfig
 */
export const SCHEDULED_TASK_MODEL_VALUES = ["opus", "sonnet", "haiku"] as const;
export type ScheduledTaskModel = (typeof SCHEDULED_TASK_MODEL_VALUES)[number];

/**
 * Valid `kind` values for a scheduled-task entry. See
 * `ScheduledTaskEntry.kind` for semantics.
 */
export const SCHEDULED_TASK_KIND_VALUES = ["issue-worker", "pipeline"] as const;
export type ScheduledTaskKind = (typeof SCHEDULED_TASK_KIND_VALUES)[number];

/**
 * Default `kind` applied when a `ScheduledTaskEntry` does not declare
 * one. The overwhelming majority of registered tasks delegate work to
 * the generic `issue-worker`; only end-to-end pipeline managers like
 * the orchestrator opt into `kind: "pipeline"`.
 */
export const DEFAULT_SCHEDULED_TASK_KIND: ScheduledTaskKind = "issue-worker";

/**
 * Default root directory (relative to the repo root) for scheduled-task
 * files. Mirrors the vortex layout — one directory per task at
 * `.claude/scheduled-tasks/<taskId>/SKILL.md`.
 *
 * @see ScheduledTasksConfig
 */
export const DEFAULT_SCHEDULED_TASKS_ROOT = ".claude/scheduled-tasks";

/**
 * Off-peak cron sample surfaced in the rendered documentation. Every
 * 20 minutes during off-peak hours (before 08:00 and after 14:00
 * local). Informational only — tasks ship disabled by default with
 * no cron, so the consumer must opt in and set a cron explicitly.
 *
 * @see ScheduledTasksConfig
 */
export const DEFAULT_OFF_PEAK_CRON_EXAMPLE = "3,23,43 0-7,14-23 * * *";

/**
 * A single fully-resolved scheduled-task entry. Returned by
 * `resolveScheduledTasks()`; the rendered documentation section and
 * the emitted `.claude/scheduled-tasks/<taskId>/SKILL.md` files are
 * derived from this list.
 */
export interface ResolvedScheduledTask {
  /**
   * Unique task directory name. Emitted under
   * `<root>/<taskId>/SKILL.md`. Derived from the sub-agent name by
   * default (e.g. `company-profile-analyst` → `worker-company-profile`).
   */
  readonly taskId: string;
  /**
   * Target sub-agent name (e.g. `company-profile-analyst`). The task's
   * rendered prompt points operators at `.claude/agents/<agent>.md`.
   */
  readonly agent: string;
  /**
   * Human-readable agent label for rendered tables and frontmatter
   * descriptions (e.g. `"Company Profile"`).
   */
  readonly agentLabel: string;
  /**
   * Primary GitHub `type:*` label (without the `type:` prefix). When
   * `typeLabels` is unset, this is the sole type filter; when
   * `typeLabels` is set, it is the first element.
   */
  readonly typeLabel: string;
  /**
   * Exact `type:*` label values (without the `type:` prefix) the
   * worker picks up. When set (`length >= 1`), represents a
   * multi-type filter (e.g. routing-bucket `worker-issue`). When
   * unset, the `typeLabel` single-value filter applies.
   */
  readonly typeLabels?: ReadonlyArray<string>;
  /**
   * Optional phase-label prefix (e.g. `company:`). When present and
   * `phaseLabels` is unset, the task's SKILL.md instructs the worker
   * to filter on both `type:<typeLabel>` **and** any `<phasePrefix>*`
   * label. Ignored when `phaseLabels` is set.
   */
  readonly phasePrefix?: string;
  /**
   * Exact phase-label values the worker filters on (e.g.
   * `["req:write"]`). When set (`length >= 1`), takes precedence over
   * `phasePrefix`. When unset, the `phasePrefix` prefix-match
   * applies.
   */
  readonly phaseLabels?: ReadonlyArray<string>;
  /**
   * Recommended model tier. Surfaced on the task's SKILL.md
   * frontmatter and in the rendered documentation table.
   */
  readonly recommendedModel: ScheduledTaskModel;
  /** Whether the task is emitted to disk. Default: `false`. */
  readonly enabled: boolean;
  /**
   * Optional cron expression. When `null` the task is manual-only
   * (runs only when an operator invokes it explicitly).
   */
  readonly cron: string | null;
  /**
   * Short one-line description for the rendered table and SKILL.md
   * frontmatter.
   */
  readonly description: string;
  /**
   * Discriminator controlling how the SKILL.md body and the registered-
   * tasks table cell are rendered. `"issue-worker"` (default) emits the
   * standard delegate-to-issue-worker prompt; `"pipeline"` emits a
   * pipeline-manager prompt that points the operator at the target
   * sub-agent's full workflow and renders `_(none — pipeline manager)_`
   * for the type-label cell.
   */
  readonly kind: ScheduledTaskKind;
}

/**
 * Canonical default registry of scheduled-task entries. One entry per
 * agent bundle that ships with configulator. Every entry is
 * **disabled by default** — consumers must explicitly opt in via
 * `ScheduledTasksConfig.overrides[taskId].enabled = true`.
 *
 * The registry mirrors the funnel-tier table in `tiers.ts` so the
 * dispatch ordering, scheduled-task filter, and orchestrator queue
 * scan all agree on the type-label taxonomy.
 */
export const DEFAULT_SCHEDULED_TASK_ENTRIES: ReadonlyArray<ResolvedScheduledTask> =
  [
    // Tier 0 — routing (issue-worker and orchestrator).
    {
      taskId: "worker-issue",
      agent: "issue-worker",
      agentLabel: "Issue Worker",
      typeLabel: "feat",
      typeLabels: [
        "feat",
        "fix",
        "chore",
        "refactor",
        "docs",
        "release",
        "hotfix",
      ],
      kind: "issue-worker",
      recommendedModel: "sonnet",
      enabled: false,
      cron: null,
      description:
        "Generic issue worker for feat/fix/chore/refactor/docs/release/hotfix routing issues.",
    },
    {
      taskId: "worker-orchestrator",
      agent: "orchestrator",
      agentLabel: "Orchestrator",
      // Pipeline tasks do not filter on a `type:*` label — the
      // orchestrator runs an end-to-end cycle every invocation. The
      // `typeLabel` field is required by the registry shape so we
      // store the routing-bucket sentinel `feat`; rendered output
      // uses the `_(none — pipeline manager)_` cell instead.
      typeLabel: "feat",
      kind: "pipeline",
      recommendedModel: "sonnet",
      enabled: false,
      cron: null,
      description:
        "End-to-end pipeline manager: triage, maintenance, queue scan, delegate the picked issue to the issue-worker, then cleanup.",
    },
    // Tier 1 — research.
    {
      taskId: "worker-research",
      agent: "research-analyst",
      agentLabel: "Research",
      typeLabel: "research",
      phasePrefix: "research:",
      kind: "issue-worker",
      recommendedModel: "opus",
      enabled: false,
      cron: null,
      description:
        "Generic research pipeline — scope, slice, verify phases tracked by research:* labels.",
    },
    {
      taskId: "worker-industry-discovery",
      agent: "industry-discovery-analyst",
      agentLabel: "Industry Discovery",
      typeLabel: "industry-discovery",
      phasePrefix: "industry:",
      kind: "issue-worker",
      recommendedModel: "opus",
      enabled: false,
      cron: null,
      description:
        "Industry-vertical discovery — discover, evaluate, plan phases.",
    },
    {
      taskId: "worker-standards-research",
      agent: "standards-research-analyst",
      agentLabel: "Standards Research",
      typeLabel: "standards-research",
      phasePrefix: "standards:",
      kind: "issue-worker",
      recommendedModel: "opus",
      enabled: false,
      cron: null,
      description: "Standards / framework research pipeline.",
    },
    {
      taskId: "worker-regulatory-research",
      agent: "regulatory-research-analyst",
      agentLabel: "Regulatory Research",
      typeLabel: "regulatory-research",
      phasePrefix: "regulatory:",
      kind: "issue-worker",
      recommendedModel: "opus",
      enabled: false,
      cron: null,
      description: "Regulatory-framework research pipeline.",
    },
    // Tier 2 — profiles.
    {
      taskId: "worker-company-profile",
      agent: "company-profile-analyst",
      agentLabel: "Company Profile",
      typeLabel: "company-profile",
      phasePrefix: "company:",
      kind: "issue-worker",
      recommendedModel: "sonnet",
      enabled: false,
      cron: null,
      description:
        "Company research, drafting, followup, match, refresh, and competitive analysis phases.",
    },
    {
      taskId: "worker-people-profile",
      agent: "people-profile-analyst",
      agentLabel: "People Profile",
      typeLabel: "people-profile",
      phasePrefix: "people:",
      kind: "issue-worker",
      recommendedModel: "sonnet",
      enabled: false,
      cron: null,
      description: "Person research and drafting phases.",
    },
    {
      taskId: "worker-software-profile",
      agent: "software-profile-analyst",
      agentLabel: "Software Profile",
      typeLabel: "software-profile",
      phasePrefix: "software:",
      kind: "issue-worker",
      recommendedModel: "sonnet",
      enabled: false,
      cron: null,
      description:
        "Software product research, profiling, feature-matrix, capability mapping, and followup phases.",
    },
    {
      taskId: "worker-customer-profile",
      agent: "customer-profile-analyst",
      agentLabel: "Customer Profile",
      typeLabel: "customer-profile",
      phasePrefix: "customer:",
      kind: "issue-worker",
      recommendedModel: "sonnet",
      enabled: false,
      cron: null,
      description: "Customer discovery, profiling, and analysis phases.",
    },
    // Tier 3 — synthesis.
    {
      taskId: "worker-business-models",
      agent: "business-models-analyst",
      agentLabel: "Business Models",
      typeLabel: "business-model",
      phasePrefix: "business-models:",
      kind: "issue-worker",
      recommendedModel: "opus",
      enabled: false,
      cron: null,
      description: "Business-model canvas authoring and segment synthesis.",
    },
    {
      taskId: "worker-bcm-writer",
      agent: "bcm-writer",
      agentLabel: "BCM Writer",
      typeLabel: "bcm-document",
      phasePrefix: "bcm:",
      kind: "issue-worker",
      recommendedModel: "sonnet",
      enabled: false,
      cron: null,
      description:
        "Business capability-model document authoring phases (outline, scaffold, context, connect).",
    },
    {
      taskId: "worker-requirements-analyst",
      agent: "requirements-analyst",
      agentLabel: "Requirements Analyst",
      typeLabel: "requirement",
      phasePrefix: "req:",
      kind: "issue-worker",
      recommendedModel: "opus",
      enabled: false,
      cron: null,
      description: "Requirements gap discovery — scan, draft-trace phases.",
    },
    {
      taskId: "worker-requirements-writer",
      agent: "requirements-writer",
      agentLabel: "Requirements Writer",
      typeLabel: "requirement",
      phaseLabels: ["req:write"],
      kind: "issue-worker",
      recommendedModel: "sonnet",
      enabled: false,
      cron: null,
      description: "Requirements document authoring (req:write label).",
    },
    {
      taskId: "worker-requirements-reviewer",
      agent: "requirements-reviewer",
      agentLabel: "Requirements Reviewer",
      typeLabel: "requirement",
      phaseLabels: ["req:review", "req:deprecate"],
      kind: "issue-worker",
      recommendedModel: "opus",
      enabled: false,
      cron: null,
      description:
        "Requirements review and deprecation phases (req:review / req:deprecate).",
    },
    // Tier 4 — support.
    {
      taskId: "worker-meeting-analysis",
      agent: "meeting-analyst",
      agentLabel: "Meeting Analysis",
      typeLabel: "meeting-processing",
      phasePrefix: "meeting:",
      kind: "issue-worker",
      recommendedModel: "sonnet",
      enabled: false,
      cron: null,
      description:
        "Meeting transcript processing pipeline (extract, notes, draft, link).",
    },
    {
      taskId: "worker-agenda",
      agent: "agenda-analyst",
      agentLabel: "Agenda",
      typeLabel: "agenda",
      phasePrefix: "agenda:",
      kind: "issue-worker",
      recommendedModel: "sonnet",
      enabled: false,
      cron: null,
      description: "Meeting-agenda drafting pipeline.",
    },
    {
      taskId: "worker-maintenance",
      agent: "maintenance-audit",
      agentLabel: "Maintenance",
      typeLabel: "maintenance",
      phasePrefix: "maint:",
      kind: "issue-worker",
      recommendedModel: "haiku",
      enabled: false,
      cron: null,
      description:
        "Documentation-maintenance audit pipeline (scan, fix, verify phases).",
    },
    {
      taskId: "worker-pr-review",
      agent: "pr-reviewer",
      agentLabel: "PR Review",
      typeLabel: "pr-review",
      kind: "issue-worker",
      recommendedModel: "haiku",
      enabled: false,
      cron: null,
      description:
        "Scans open PRs and reviews / merges eligible ones. No type-label filter.",
    },
    {
      taskId: "worker-docs-sync",
      agent: "docs-sync",
      agentLabel: "Docs Sync",
      typeLabel: "docs-sync",
      phasePrefix: "docs-sync:",
      kind: "issue-worker",
      recommendedModel: "sonnet",
      enabled: false,
      cron: null,
      description:
        "Documentation-sync drift-detection and audit pipeline (scaffolding release — behavior carried by downstream child issues of the parent docs-sync epic).",
    },
  ];

/**
 * Fully-resolved scheduled-tasks settings. Every field is defaulted so
 * downstream renderers can reason about a single canonical shape.
 */
export interface ResolvedScheduledTasks {
  readonly enabled: boolean;
  readonly root: string;
  readonly tasks: ReadonlyArray<ResolvedScheduledTask>;
}

/**
 * Resolve a (possibly absent) `ScheduledTasksConfig` into a canonical
 * `ResolvedScheduledTasks` with every field filled in. Unset fields
 * cascade from their documented defaults.
 *
 * The resolver merges three sources in this order:
 *
 * 1. `DEFAULT_SCHEDULED_TASK_ENTRIES` — the built-in per-agent registry.
 * 2. `config.overrides` — per-task consumer overrides keyed by `taskId`.
 * 3. `config.tasks` — fully consumer-authored entries. Entries whose
 *    `taskId` matches a default entry **replace** it; entries with a new
 *    `taskId` are appended.
 *
 * Malformed configs (unknown model, empty taskId/agent/typeLabel,
 * duplicate taskIds in a single supplied list) throw a descriptive
 * `Error` — callers should not need to guard against it at runtime.
 */
export function resolveScheduledTasks(
  config?: ScheduledTasksConfig,
  excludeBundles: ReadonlyArray<string> = [],
): ResolvedScheduledTasks {
  const root = config?.root ?? DEFAULT_SCHEDULED_TASKS_ROOT;
  assertValidRoot(root);

  const merged = new Map<string, ResolvedScheduledTask>();
  for (const entry of DEFAULT_SCHEDULED_TASK_ENTRIES) {
    // Drop default entries whose target bundle has been excluded.
    // Consumer-supplied entries via `tasks` and per-task overrides
    // pass through unchanged so a consumer can still wire a custom
    // worker against an excluded bundle's type label if they really
    // want to.
    if (isScheduledTaskOwnedByExcluded(entry.taskId, excludeBundles)) {
      continue;
    }
    merged.set(entry.taskId, entry);
  }

  if (config?.overrides) {
    for (const [taskId, override] of Object.entries(config.overrides)) {
      const existing = merged.get(taskId);
      if (!existing) {
        // The taskId may be unknown because its bundle was excluded.
        // Skip the override silently in that case so consumers can
        // declare overrides defensively without tracking exclusions.
        if (isScheduledTaskOwnedByExcluded(taskId, excludeBundles)) {
          continue;
        }
        throw new Error(
          `ScheduledTasksConfig.overrides references unknown taskId ${JSON.stringify(
            taskId,
          )}; override entries must match a default taskId. Supply full entries via \`tasks\` to add new tasks.`,
        );
      }
      merged.set(taskId, applyOverride(existing, override, taskId));
    }
  }

  if (config?.tasks) {
    const seen = new Set<string>();
    for (const entry of config.tasks) {
      const resolved = validateFullEntry(entry);
      if (seen.has(resolved.taskId)) {
        throw new Error(
          `ScheduledTasksConfig.tasks declares taskId ${JSON.stringify(
            resolved.taskId,
          )} more than once; every taskId must appear at most once within \`tasks\`.`,
        );
      }
      seen.add(resolved.taskId);
      merged.set(resolved.taskId, resolved);
    }
  }

  return {
    enabled: config?.enabled ?? true,
    root,
    tasks: [...merged.values()],
  };
}

/**
 * Synth-time validation hook. Throws a descriptive `Error` when the
 * supplied `ScheduledTasksConfig` is malformed. Called by
 * `AgentConfig.preSynthesize` before any rendering so a misconfigured
 * scheduled-tasks table fails the build instead of silently shipping
 * broken worker prompts. Returns the resolved config unchanged so
 * callers can write `const st = validateScheduledTasksConfig(config)` in
 * one line.
 *
 * Malformed cases rejected here:
 *
 * - `root` empty, whitespace-only, or absolute.
 * - `overrides[taskId]` referencing an unknown `taskId`.
 * - Consumer-supplied `tasks` entry with unknown model, empty
 *   `taskId` / `agent` / `typeLabel`, or cron string that is not a
 *   plain string.
 * - Duplicate `taskId` values within the supplied `tasks` list.
 */
export function validateScheduledTasksConfig(
  config?: ScheduledTasksConfig,
  excludeBundles: ReadonlyArray<string> = [],
): ResolvedScheduledTasks {
  return resolveScheduledTasks(config, excludeBundles);
}

/**
 * Render the markdown subsection appended to the
 * `orchestrator-conventions` rule. Always returns a non-empty string —
 * the default registry is non-empty so the section documents the
 * per-agent scheduled-task layout even when every task is disabled
 * (the default).
 */
export function renderScheduledTasksSection(
  resolved: ResolvedScheduledTasks,
): string {
  const lines: Array<string> = [
    "## Per-agent scheduled tasks",
    "",
    "Each agent bundle that ships with configulator can emit a",
    "**scheduled-task definition** under",
    `\`${resolved.root}/<taskId>/SKILL.md\`. The definition carries:`,
    "",
    "- A **label filter** — the `type:*` label (and optional phase-label",
    "  prefix) the worker consults when picking up an issue.",
    "- A **recommended model** — rendered on the task's frontmatter so",
    "  operators know which model tier to launch the task against.",
    "- An optional **cron** expression — when absent, the task is",
    "  manual-only (runs only when invoked explicitly).",
    "",
    "Every task ships **disabled by default**. Consumers opt in via",
    "`AgentConfigOptions.scheduledTasks.overrides.<taskId>.enabled =",
    "true`. Only enabled tasks are emitted to disk; disabled tasks",
    "leave no trace in the generated repo.",
    "",
  ];

  if (!resolved.enabled) {
    lines.push(
      "**The scheduled-tasks subsystem is disabled for this project.**",
      "No `<root>/<taskId>/SKILL.md` files are emitted regardless of",
      "individual task overrides. Enable via",
      "`AgentConfigOptions.scheduledTasks.enabled = true`.",
      "",
    );
    return lines.join("\n");
  }

  lines.push(
    "### Recommended-model caveat",
    "",
    "The Claude Code scheduled-task runtime does **not** currently pin",
    "a model per task — each task runs on whatever model the runtime",
    "is launched with. The recommended-model column below is",
    "informational; splitting workers by type still delivers two",
    "practical benefits:",
    "",
    "1. **Independent cadence.** Expensive Opus workers can run less",
    "   frequently than cheap Haiku workers.",
    "2. **Clean opt-in/opt-out.** Disable an entire agent category by",
    "   toggling one task — no label surgery needed.",
    "",
    "To route each task to its recommended model today, the operator",
    "runs a scheduler instance per model tier (Opus / Sonnet / Haiku)",
    "and enables only the tasks belonging to that tier in each instance.",
    "",
    "### Off-peak cron example",
    "",
    `A reasonable off-peak cadence is \`${DEFAULT_OFF_PEAK_CRON_EXAMPLE}\` —`,
    "every 20 minutes outside the 08:00–14:00 local window. Override",
    "per-task via",
    "`AgentConfigOptions.scheduledTasks.overrides.<taskId>.cron`.",
    "",
    "### Registered tasks",
    "",
    "| Task ID | Target agent | `type:*` filter | Phase prefix | Model | Enabled | Cron |",
    "|---------|--------------|-----------------|--------------|-------|---------|------|",
  );

  for (const task of resolved.tasks) {
    let typeCell: string;
    let phaseCell: string;
    if (task.kind === "pipeline") {
      typeCell = "_(none — pipeline manager)_";
      phaseCell = "_(none — pipeline manager)_";
    } else {
      typeCell =
        task.typeLabels && task.typeLabels.length > 1
          ? task.typeLabels.map((l) => `\`type:${l}\``).join(", ")
          : `\`type:${task.typeLabel}\``;
      if (task.phaseLabels && task.phaseLabels.length > 0) {
        phaseCell = task.phaseLabels.map((l) => `\`${l}\``).join(", ");
      } else if (task.phasePrefix) {
        phaseCell = `\`${task.phasePrefix}*\``;
      } else {
        phaseCell = "_(any)_";
      }
    }
    const cronCell = task.cron ? `\`${task.cron}\`` : "_manual-only_";
    lines.push(
      `| \`${task.taskId}\` | \`${task.agent}\` | ${typeCell} | ${phaseCell} | ${task.recommendedModel} | ${task.enabled ? "yes" : "no"} | ${cronCell} |`,
    );
  }

  return lines.join("\n");
}

/**
 * Render the body of a single scheduled-task `SKILL.md` file. The
 * frontmatter carries the task name, description, and recommended
 * model; the body teaches the worker which label filter to apply and
 * which agent file to route issues to.
 *
 * The output is the full file contents — including the leading
 * frontmatter delimiter — so callers can hand it straight to a
 * `TextFile`.
 */
export function renderScheduledTaskSkillFile(
  task: ResolvedScheduledTask,
): string {
  const lines: Array<string> = [
    "---",
    `name: ${task.taskId}`,
    `description: ${task.description}`,
    `model: ${task.recommendedModel}`,
  ];
  if (task.cron) {
    lines.push(`cron: "${task.cron}"`);
  }
  lines.push("---", "");

  if (task.kind === "pipeline") {
    return renderPipelineSkillBody(task, lines);
  }
  return renderIssueWorkerSkillBody(task, lines);
}

function renderIssueWorkerSkillBody(
  task: ResolvedScheduledTask,
  lines: Array<string>,
): string {
  lines.push(
    `# ${task.agentLabel} Scheduled Worker`,
    "",
    `You are the **${task.agentLabel}** scheduled worker. Follow the`,
    "full workflow in `.claude/agents/issue-worker.md` (clean starting",
    "state → unblock → PR review → find-oldest-unblocked-issue → route",
    "to agent → one unit of work), with the label filter below applied",
    "at the find-an-issue step.",
    "",
    "## Label filter",
    "",
    "Only consider issues matching **both**:",
    "",
  );
  if (task.typeLabels && task.typeLabels.length > 1) {
    const formatted = task.typeLabels.map((l) => `\`type:${l}\``).join(", ");
    lines.push(`- Any of: ${formatted}`);
  } else {
    lines.push(`- Label: \`type:${task.typeLabel}\``);
  }
  if (task.phaseLabels && task.phaseLabels.length > 0) {
    const formatted = task.phaseLabels.map((l) => `\`${l}\``).join(", ");
    const noun = task.phaseLabels.length === 1 ? "label" : "labels";
    lines.push(`- Exact phase ${noun}: ${formatted}`);
  } else if (task.phasePrefix) {
    lines.push(
      `- Any phase label matching \`${task.phasePrefix}*\` (e.g. \`${task.phasePrefix}${SAMPLE_PHASE}\`).`,
    );
  } else {
    lines.push("- No phase-label filter — all matching issues are eligible.");
  }

  lines.push(
    "",
    `Route matched issues to \`.claude/agents/${task.agent}.md\` and`,
    "execute its phase-specific workflow.",
    "",
  );

  if (task.taskId === "worker-issue") {
    lines.push(
      "## Feedback-pull scan (runs before issue selection)",
      "",
      "Because you run autonomously (scheduled mode), **also scan for PRs",
      "needing feedback work before scanning the issue queue** — and",
      "prefer them. The pr-reviewer hands fixes off by applying a durable",
      "`review:needs-worker` marker on the PR (it cannot spawn you in",
      "session). Pull that marker instead of waiting for an explicit push:",
      "",
      "```bash",
      "gh pr list --state open --label 'review:needs-worker' \\",
      "  --json number,labels,headRefName,updatedAt",
      "```",
      "",
      "If any open PR carries an unconsumed `review:needs-worker` fix-list,",
      "enter **feedback mode** for the highest-priority match (no explicit",
      "`feedback mode: PR #<n>` push required) and run the issue-worker's",
      "feedback-mode phases: apply the fix-list, push, write the",
      "worker-report comment, and **clear `review:needs-worker`** (leave",
      "`review:fixing` for the reviewer's confirm pass). Only when no PR",
      "needs a worker do you fall through to the issue-queue selection",
      "above. See the **Invocation Mode** and **Phase 1** sections of",
      "`.claude/agents/issue-worker.md` for the full contract.",
      "",
    );
  }

  lines.push(
    "## Recommended model",
    "",
    `**${capitalize(task.recommendedModel)}** — see`,
    `\`.claude/agents/${task.agent}.md\` for the agent's full model`,
    "rationale. The operator launches the scheduler instance on the",
    "recommended model; this task does not pin a model at the runtime",
    "level.",
    "",
    "## Exit conditions",
    "",
    "Exit cleanly after completing one unit of work (or immediately if",
    "no eligible issue exists). Do not pick up issues outside the",
    "filter above — leave them for other workers.",
  );

  return lines.join("\n");
}

function renderPipelineSkillBody(
  task: ResolvedScheduledTask,
  lines: Array<string>,
): string {
  lines.push(
    `# ${task.agentLabel} Pipeline Worker`,
    "",
    `You are the **${task.agentLabel}** pipeline worker. This task is a`,
    "**pipeline manager**, not an issue-worker delegator — it does not",
    "filter issues by `type:*` or phase label. Each invocation runs the",
    `target sub-agent's full end-to-end cycle exactly once.`,
    "",
    "## Depth-0 execution contract",
    "",
    "**You — the scheduled-task session — are the pipeline manager.**",
    "Do **not** spawn the target sub-agent via the `Agent` tool. The",
    "Claude Code runtime forbids nested sub-agent spawning: a depth-1",
    "sub-agent cannot invoke `Agent` to reach depth-2, so a pipeline",
    "manager that delegates to other workers must run at depth-0 (this",
    "scheduled-task session). See",
    '<https://code.claude.com/docs/en/sub-agents> — *"Subagents cannot',
    "spawn other subagents. If your workflow requires nested delegation,",
    'use Skills or chain subagents from the main conversation."*',
    "",
    "Therefore:",
    "",
    `1. **Read \`.claude/agents/${task.agent}.md\` in this session** and`,
    "   execute its phase pipeline directly. Treat the agent file as a",
    "   workflow runbook the scheduled-task session follows step-by-step,",
    "   not as a sub-agent to spawn.",
    "2. **Use the `Agent` tool (with the `subagent_type` parameter set",
    "   to the target worker name) to delegate one tier down.** From",
    "   depth-0, you can spawn a depth-1 sub-agent (e.g. `issue-worker`,",
    "   `pr-reviewer`) for the implementation, review, or merge step the",
    "   pipeline calls out.",
    "3. **Never spawn the pipeline manager itself as a sub-agent.** Do",
    `   not call \`Agent(subagent_type: "${task.agent}", ...)\`. That`,
    "   would put the pipeline manager at depth-1 and break every",
    "   subsequent delegation step.",
    "",
    "## Recommended model",
    "",
    `**${capitalize(task.recommendedModel)}** — see`,
    `\`.claude/agents/${task.agent}.md\` for the agent's full model`,
    "rationale. The operator launches the scheduler instance on the",
    "recommended model; this task does not pin a model at the runtime",
    "level.",
    "",
    "## Exit conditions",
    "",
    "Exit cleanly after one full cycle of the target pipeline (or",
    "immediately if the pipeline reports there is no eligible work).",
    "Do not loop — the next scheduled invocation runs another cycle.",
  );

  return lines.join("\n");
}

/*******************************************************************************
 *
 * Internal helpers
 *
 ******************************************************************************/

const SAMPLE_PHASE = "phase1";

function capitalize(value: string): string {
  if (value.length === 0) {
    return value;
  }
  return value[0].toUpperCase() + value.slice(1);
}

function applyOverride(
  base: ResolvedScheduledTask,
  override: ScheduledTaskOverride,
  taskId: string,
): ResolvedScheduledTask {
  if (override.recommendedModel !== undefined) {
    assertValidModel(override.recommendedModel, taskId);
  }
  if (override.cron !== undefined && override.cron !== null) {
    assertValidCron(override.cron, taskId);
  }
  if (override.typeLabels !== undefined) {
    assertNonEmptyLabelList(override.typeLabels, taskId, "typeLabels");
  }
  if (override.phaseLabels !== undefined) {
    assertNonEmptyLabelList(override.phaseLabels, taskId, "phaseLabels");
  }
  if (override.kind !== undefined) {
    assertValidKind(override.kind, taskId);
  }
  const resolvedTypeLabels =
    override.typeLabels !== undefined
      ? normalizeLabelList(override.typeLabels)
      : base.typeLabels;
  const primaryTypeLabel = resolvedTypeLabels?.[0] ?? base.typeLabel;
  return {
    ...base,
    enabled: override.enabled ?? base.enabled,
    cron: override.cron === undefined ? base.cron : override.cron,
    recommendedModel: override.recommendedModel ?? base.recommendedModel,
    description: override.description ?? base.description,
    typeLabel: primaryTypeLabel,
    typeLabels: resolvedTypeLabels,
    phaseLabels:
      override.phaseLabels !== undefined
        ? normalizeLabelList(override.phaseLabels)
        : base.phaseLabels,
    kind: override.kind ?? base.kind,
  };
}

function validateFullEntry(entry: ScheduledTaskEntry): ResolvedScheduledTask {
  const taskId = (entry.taskId ?? "").trim();
  if (taskId.length === 0) {
    throw new Error(
      "ScheduledTasksConfig.tasks entry has an empty or whitespace-only `taskId`; every entry must declare a non-empty taskId.",
    );
  }
  const agent = (entry.agent ?? "").trim();
  if (agent.length === 0) {
    throw new Error(
      `ScheduledTasksConfig.tasks entry for taskId ${JSON.stringify(taskId)} has an empty or whitespace-only \`agent\`; every entry must target a sub-agent name.`,
    );
  }
  const typeLabel = (entry.typeLabel ?? "").trim();
  if (typeLabel.length === 0) {
    throw new Error(
      `ScheduledTasksConfig.tasks entry for taskId ${JSON.stringify(taskId)} has an empty or whitespace-only \`typeLabel\`; every entry must declare a type:* label value.`,
    );
  }
  assertValidModel(entry.recommendedModel, taskId);
  if (entry.cron !== undefined && entry.cron !== null) {
    assertValidCron(entry.cron, taskId);
  }
  if (entry.typeLabels !== undefined) {
    assertNonEmptyLabelList(entry.typeLabels, taskId, "typeLabels");
  }
  if (entry.phaseLabels !== undefined) {
    assertNonEmptyLabelList(entry.phaseLabels, taskId, "phaseLabels");
  }
  if (entry.kind !== undefined) {
    assertValidKind(entry.kind, taskId);
  }
  const typeLabels =
    entry.typeLabels !== undefined
      ? normalizeLabelList(entry.typeLabels)
      : undefined;
  const primaryTypeLabel = typeLabels?.[0] ?? typeLabel;
  return {
    taskId,
    agent,
    agentLabel: entry.agentLabel ?? agent,
    typeLabel: primaryTypeLabel,
    typeLabels,
    phasePrefix: entry.phasePrefix?.trim() || undefined,
    phaseLabels:
      entry.phaseLabels !== undefined
        ? normalizeLabelList(entry.phaseLabels)
        : undefined,
    recommendedModel: entry.recommendedModel,
    enabled: entry.enabled ?? false,
    cron: entry.cron ?? null,
    description: entry.description ?? `${agent} scheduled worker.`,
    kind: entry.kind ?? DEFAULT_SCHEDULED_TASK_KIND,
  };
}

function assertValidKind(
  value: string,
  taskId: string,
): asserts value is ScheduledTaskKind {
  if (!(SCHEDULED_TASK_KIND_VALUES as ReadonlyArray<string>).includes(value)) {
    throw new Error(
      `ScheduledTasksConfig entry for taskId ${JSON.stringify(taskId)} has kind ${JSON.stringify(
        value,
      )}; must be one of ${SCHEDULED_TASK_KIND_VALUES.join(" | ")}.`,
    );
  }
}

function assertValidModel(
  value: string,
  taskId: string,
): asserts value is ScheduledTaskModel {
  if (!(SCHEDULED_TASK_MODEL_VALUES as ReadonlyArray<string>).includes(value)) {
    throw new Error(
      `ScheduledTasksConfig entry for taskId ${JSON.stringify(taskId)} has recommendedModel ${JSON.stringify(
        value,
      )}; must be one of ${SCHEDULED_TASK_MODEL_VALUES.join(" | ")}.`,
    );
  }
}

function assertValidCron(value: string, taskId: string): void {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(
      `ScheduledTasksConfig entry for taskId ${JSON.stringify(taskId)} has cron ${JSON.stringify(
        value,
      )}; cron must be a non-empty string or null.`,
    );
  }
}

function assertValidRoot(root: string): void {
  const trimmed = root.trim();
  if (trimmed.length === 0) {
    throw new Error(
      "ScheduledTasksConfig.root must be a non-empty string relative to the repo root",
    );
  }
  if (trimmed.startsWith("/")) {
    throw new Error(
      `ScheduledTasksConfig.root must be relative to the repo root (no leading '/'); got ${root}`,
    );
  }
}

function assertNonEmptyLabelList(
  labels: ReadonlyArray<string>,
  taskId: string,
  field: "typeLabels" | "phaseLabels",
): void {
  if (labels.length === 0) {
    throw new Error(
      `ScheduledTasksConfig entry for taskId ${JSON.stringify(taskId)} has an empty \`${field}\` array; supply at least one label or omit the field to keep the default.`,
    );
  }
  for (const label of labels) {
    if (typeof label !== "string" || label.trim().length === 0) {
      throw new Error(
        `ScheduledTasksConfig entry for taskId ${JSON.stringify(taskId)} has an empty or whitespace-only value in \`${field}\`; every entry must be a non-empty string.`,
      );
    }
  }
}

function normalizeLabelList(
  labels: ReadonlyArray<string>,
): ReadonlyArray<string> {
  return labels.map((l) => l.trim());
}
